import React, { useEffect, useMemo, useState } from 'react';
import type { RuleGroupType, RuleType } from 'react-querybuilder';
import { Formik, useFormikContext } from 'formik';
import { isEmpty, isNil } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import Chip from '~/components/core/Atomic/Chip/Chip';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik, {
  concreteSelectedLobs,
  isAllLobsSelected,
} from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import SubOrgMultiSelectWithChipsFormik, {
  isAllSubOrganizationsSelected,
} from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import { GUARDRAIL_OUTCOMES } from '~/components/Guardrails/types';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import type {
  GuardrailRuleCreateParams,
  GuardrailRuleInfo,
  GuardrailSupportedEventsResponseGuardrailSupportedEventModel,
} from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';
import { sortKeysByField } from '~/Utils/sortKeysByField';

import CardDialog from '../../../CardDialog';
import useOrganization from '../../../OrganizationContext';
import TextFieldFormik from '../../../TextFieldFormik';

import GuardrailConditionsQueryBuilderFormik from './QueryBuilder/GuardrailConditionsQueryBuilderFormik';
import { AI_AGENT_CONDITION_FIELD, hasAiConditionReference } from './aiConditions';

interface FormValues {
  id: number | null;
  name: string;
  trigger_event: string;
  outcome: '' | GuardrailRuleInfo['outcome'];
  message: string;
  sub_organization_ids: number[];
  // String LOB keys, plus the widget's numeric "All" sentinel when every LOB is
  // selected — the same mixed shape user interaction already produces. Strip the
  // sentinel with `concreteSelectedLobs` before anything leaves the form.
  lobs: (string | number)[];
  is_all_lobs: boolean;
  is_enabled: boolean;
  // Opaque RQB JSON tree written by the shared conditions builder.
  conditions_json?: RuleGroupType | null;
}

export const FIELD_IDS = {
  ID: 'id',
  NAME: 'name',
  TRIGGER_EVENT: 'trigger_event',
  OUTCOME: 'outcome',
  MESSAGE: 'message',
  SUB_ORGANIZATION_IDS: 'sub_organization_ids',
  LOBS: 'lobs',
  ALL_LOBS: 'is_all_lobs',
  IS_ENABLED: 'is_enabled',
  CONDITIONS_JSON: 'conditions_json',
} as const;

/** Why Warning is not selectable once the rule reads the AI context. Shown under the option so the
 *  greyed-out radio is not left unexplained. */
const AI_RULE_BLOCK_ONLY_NOTE =
  'Not available: this rule is scoped to AI-agent calls, and an agent cannot review a message or supply an override reason.';

/** The Type options, with Warning withdrawn for a rule an agent's call can match.
 *
 *  Such a condition can only ever match an agent call — `build_context` binds
 *  `is_ai_agent: false` with no confidence leaf for a person — and an agent has no interactive
 *  channel to acknowledge a warning and record an override reason. A warn outcome on such a rule
 *  would therefore let every matching call through while reading, on this screen, as a guardrail.
 *
 *  Not every `ai.*` condition qualifies: `ai.is_ai_agent` tested for *false* matches only the
 *  adjuster, who does have that channel, so it keeps Warning. `hasAiConditionReference` draws the
 *  line, mirroring the BE exemption that leaves the same rule authorable as a warn. */
const buildOutcomeOptions = (isAiRule: boolean) => [
  {
    optionValue: 'warn',
    isFullWidth: true,
    disabled: isAiRule,
    note: isAiRule ? AI_RULE_BLOCK_ONLY_NOTE : undefined,
    text: (
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
        <strong>Warning</strong> – Allows the action to proceed with an override. The user must review the message and
        provide a reason, which will be recorded in the claim history for audit purposes.
      </Text>
    ),
  },
  {
    optionValue: 'block',
    isFullWidth: true,
    text: (
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
        <strong>Block</strong> – Prevents the action from being completed until the rule conditions are satisfied. No
        override is available. An AI agent may take another action to satisfy the rule and retry.
      </Text>
    ),
  },
  {
    optionValue: 'route_to_human',
    isFullWidth: true,
    text: (
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
        <strong>Route to Human</strong> – Prevents the action from being completed, and the rule cannot be satisfied by
        whoever attempted it. An AI agent stops and raises the action for human approval instead of retrying. No
        override is available.
      </Text>
    ),
  },
];

const createValidationSchema = (subOrganizationEnabled: boolean) =>
  Yup.object().shape({
    [FIELD_IDS.NAME]: Yup.string().required('Name is required'),
    [FIELD_IDS.TRIGGER_EVENT]: Yup.string().required('Event is required'),
    [FIELD_IDS.OUTCOME]: Yup.string()
      .oneOf([...GUARDRAIL_OUTCOMES], 'Type is required')
      .required('Type is required'),
    [FIELD_IDS.MESSAGE]: Yup.string().required('Message is required'),
    [FIELD_IDS.SUB_ORGANIZATION_IDS]: subOrganizationEnabled
      ? Yup.array().min(1, 'At least one sub organization must be selected')
      : Yup.array(),
    [FIELD_IDS.LOBS]: Yup.array().when(FIELD_IDS.ALL_LOBS, {
      is: false,
      then: (schema) => schema.min(1, 'At least one LOB must be selected'),
      otherwise: (schema) => schema,
    }),
    // No conditions_json validation: an empty conditions tree is valid — the
    // rule then fires on every matching event (same semantic as the siblings).
  });

/**
 * A scope the dialog must not let the user change: the guardrail belongs to one claim action, so
 * its trigger event and its sub-org / LOB come from the action being configured. Supplying this
 * renders Context and Type read-only and forces the outcome to `block` — an AI agent has no
 * channel to acknowledge a warning, so a guardrail on an agent action can only block. The Event is
 * read-only too, unless the action reports more than one: the user then picks among those alone.
 */
export interface GuardrailRulePresetContext {
  /** The events the action reports; a new guardrail starts on the first. */
  triggerEvents: string[];
  subOrganizationIds: number[];
  lobs: string[];
  isAllLobs: boolean;
  /** The action's `guardrails_required_conditions`: seeded after the AI-agent leaf, and locked. */
  requiredConditions?: RuleType[];
}

const buildAiAgentSeedConditions = (requiredConditions: RuleType[] = []): RuleGroupType => ({
  combinator: 'and',
  rules: [{ field: AI_AGENT_CONDITION_FIELD, operator: '=', value: true }, ...requiredConditions],
});

interface AddOrEditGuardrailRuleDialogProps {
  onSubmit: (values: GuardrailRuleCreateParams) => Promise<void>;
  supportedEvents?: Record<string, GuardrailSupportedEventsResponseGuardrailSupportedEventModel>;
  onCancel: () => void;
  rule?: GuardrailRuleInfo | null;
  presetContext?: GuardrailRulePresetContext;
}

const AddOrEditGuardrailRuleDialog: React.FC<AddOrEditGuardrailRuleDialogProps> = ({
  onSubmit,
  supportedEvents,
  onCancel,
  rule,
  presetContext,
}) => {
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled: boolean };

  // An action configured at the organization level (a flat org, so no sub-organization row) has an
  // empty preset list. Saving that verbatim would scope the rule to *no* sub-organization and the
  // guardrail would never fire, so an empty preset means "every sub-organization" instead.
  const presetSubOrgIds =
    presetContext && subOrganizationEnabled
      ? isEmpty(presetContext.subOrganizationIds)
        ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
        : presetContext.subOrganizationIds
      : [];

  return (
    <Formik
      initialValues={{
        [FIELD_IDS.ID]: rule?.id ?? null,
        [FIELD_IDS.NAME]: rule?.name ?? '',
        [FIELD_IDS.TRIGGER_EVENT]: rule?.trigger_event ?? presetContext?.triggerEvents[0] ?? '',
        // A preset guardrail is always a block; an existing rule keeps whatever it was saved as.
        [FIELD_IDS.OUTCOME]: rule?.outcome ?? (presetContext ? 'block' : ''),
        [FIELD_IDS.MESSAGE]: rule?.message ?? '',
        [FIELD_IDS.SUB_ORGANIZATION_IDS]: !subOrganizationEnabled
          ? []
          : rule
            ? rule.scope?.is_all_sub_orgs
              ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
              : (rule.scope?.sub_organization_ids ?? [])
            : presetSubOrgIds,
        // An all-LOBs rule is seeded in the widget's own representation (the "All"
        // sentinel), exactly as `sub_organization_ids` is above. Seeding the server
        // shape (`lobs: [], is_all_lobs: true`) makes the widget's mount conversion
        // rewrite both fields, which the conditions-clearing listener below reads as
        // a user edit — silently discarding the stored conditions tree on every open.
        [FIELD_IDS.LOBS]:
          (rule?.scope?.is_all_lobs ?? presetContext?.isAllLobs)
            ? [LobMultiSelectWithChipsFormik.ALL_OPTION.id]
            : (rule?.scope?.lobs ?? presetContext?.lobs ?? []),
        [FIELD_IDS.ALL_LOBS]: rule?.scope?.is_all_lobs ?? presetContext?.isAllLobs ?? false,
        [FIELD_IDS.IS_ENABLED]: rule?.is_enabled ?? true,
        [FIELD_IDS.CONDITIONS_JSON]:
          (rule?.conditions as RuleGroupType | null) ??
          (presetContext ? buildAiAgentSeedConditions(presetContext.requiredConditions) : null),
      }}
      validationSchema={createValidationSchema(subOrganizationEnabled)}
      onSubmit={async (values: FormValues, formikProps) => {
        try {
          const isAllSubOrgs = subOrganizationEnabled
            ? isAllSubOrganizationsSelected(values.sub_organization_ids)
            : true;

          const submitValues: GuardrailRuleCreateParams = {
            name: values.name,
            trigger_event: values.trigger_event,
            outcome: values.outcome as GuardrailRuleInfo['outcome'],
            message: values.message,
            scope: {
              is_all_sub_orgs: isAllSubOrgs,
              sub_organization_ids: isAllSubOrgs ? [] : values.sub_organization_ids,
              is_all_lobs: values.is_all_lobs,
              lobs: values.is_all_lobs ? [] : concreteSelectedLobs(values.lobs ?? []),
            },
            conditions: values.conditions_json ?? null,
            is_enabled: values.is_enabled,
          };
          await onSubmit(submitValues);
          onCancel();
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {() => <AddRuleInner onCancel={onCancel} supportedEvents={supportedEvents} presetContext={presetContext} />}
    </Formik>
  );
};

interface AddRuleInnerProps {
  supportedEvents?: Record<string, GuardrailSupportedEventsResponseGuardrailSupportedEventModel>;
  onCancel: () => void;
  presetContext?: GuardrailRulePresetContext;
}

const AddRuleInner: React.FC<AddRuleInnerProps> = ({ supportedEvents, onCancel, presetContext }) => {
  // `operational_details` builds the field as `sub_organizations_enabled and [...]`, so an
  // organization without sub-organizations gets the boolean `false` rather than an empty list.
  // `?? []` does not catch that (`??` only replaces null/undefined), so normalise here — this is
  // the single read both array consumers below (and `ReadOnlyContext`) are fed from.
  const { subOrganizationEnabled, subOrganizations: rawSubOrganizations } = useOrganization() as {
    subOrganizationEnabled: boolean;
    subOrganizations: { id: number; name?: string }[] | false;
  };
  const subOrganizations = Array.isArray(rawSubOrganizations) ? rawSubOrganizations : [];
  const { isSubmitting, handleSubmit, values, setFieldValue } = useFormikContext<FormValues>();
  const [isLoadingConditions, setIsLoadingConditions] = useState(false);
  const { classes } = useStyles();

  const isDisabled = isSubmitting || isLoadingConditions;
  const subOrganizationsValue = useMemo(() => values.sub_organization_ids, [values.sub_organization_ids]);
  const isEditing = values.id !== null;
  const eventKey = values.trigger_event;
  const isPresetEventSelectable = (presetContext?.triggerEvents.length ?? 0) > 1;
  // "All (inc. future lobs)" is stored as the sentinel id 0, not as a LOB key, so it must not reach
  // the catalog as a LOB named "0". Read off the forwarded value rather than `is_all_lobs`: that flag
  // is set by a change listener one render later, and the intervening render would fire the fetch.
  // The same derivation feeds the `isAllLobs` flag the catalog request carries.
  const isAllLobs = isAllLobsSelected(values.lobs ?? []);
  const catalogLobs = isAllLobs ? [] : concreteSelectedLobs(values.lobs ?? []);
  // A rule whose conditions an agent's call can match, and an agent has no channel
  // to acknowledge a warning — so Block is the only outcome it may carry. The preset dialog forces
  // that already; this is the same rule on the Guardrails screen, where the author picks the
  // outcome by hand and could otherwise pair an AI condition with `warn`. The tree decides, not the
  // preset: an author who adds an AI condition to an ordinary rule reaches the same state as one
  // who opened the dialog from a claim action.
  //
  // A preset dialog is always an AI rule: it seeds the AI-agent condition and locks it, so the
  // tree can never stop referencing `ai.*`. Reading the tree as well keeps one source of truth —
  // the outcome control depends on what the conditions say, not on which screen opened the dialog.
  const isAiRule = Boolean(presetContext) || hasAiConditionReference(values.conditions_json);
  const outcomeOptions = useMemo(() => buildOutcomeOptions(isAiRule), [isAiRule]);

  // Correcting a `warn` the control can no longer express, rather than leaving it selected behind a
  // disabled radio. This marks the form dirty without the author touching anything, which is
  // intended: the correction only reaches the BE if they save, and leaving it uncorrected would
  // keep an AI guardrail that lets every matching call through. It fires only where the BE would
  // refuse the warn too — a human-only `Is AI Agent` rule is not an AI rule here, so opening one
  // to edit its name does not silently convert its outcome.
  useEffect(() => {
    if (isAiRule && values.outcome === 'warn') {
      setFieldValue(FIELD_IDS.OUTCOME, 'block');
    }
  }, [isAiRule, values.outcome, setFieldValue]);

  // reset LOBs when sub org changes
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () => {
      setFieldValue(FIELD_IDS.LOBS, []);
      setFieldValue(FIELD_IDS.ALL_LOBS, false);
    },
    runOnFirstRender: false,
  });

  // Clear the conditions tree whenever a scope dimension changes: the catalog
  // refetches on these keys (event scope filters fields; LOB/sub-org narrow
  // which providers contribute), so a tree authored under the old scope must
  // not silently carry over. A claim action's guardrail goes back to its seed
  // instead: its locked conditions are what make it the agent's guardrail.
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.TRIGGER_EVENT, FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () =>
      setFieldValue(
        FIELD_IDS.CONDITIONS_JSON,
        presetContext ? buildAiAgentSeedConditions(presetContext.requiredConditions) : null
      ),
    runOnFirstRender: false,
  });

  return (
    <CardDialog
      isDialog
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      title={`${isEditing ? 'Edit' : 'Add'} Guardrails Engine Rule`}
    >
      {/* The banner warns that changing the scope clears the conditions. With a preset context
          the scope is fixed, and the one field that can change — the event, among the action's
          own — resets the conditions to their seed rather than clearing them, so the warning
          would describe an action that is not on offer here. */}
      {presetContext ? null : (
        <AlertBanner
          className="mb-4"
          alertType="info"
          note="Editing the event, sub organizations, or line of businesses will clear all conditions."
          title="Please Note"
          withIcon
        />
      )}
      <TextFieldFormik
        id={FIELD_IDS.NAME}
        label="Name"
        className={cn(classes.textField, 'mb-20')}
        fullWidth
        disabled={isDisabled}
      />
      {presetContext ? (
        <>
          <ReadOnlyContext
            eventLabel={isPresetEventSelectable ? undefined : (supportedEvents?.[eventKey]?.desc ?? eventKey)}
            subOrganizationEnabled={subOrganizationEnabled}
            subOrganizations={subOrganizations}
          />
          {isPresetEventSelectable ? (
            <EventSelectField
              eventKeys={presetContext.triggerEvents}
              supportedEvents={supportedEvents}
              disabled={isSubmitting}
            />
          ) : null}
        </>
      ) : (
        <>
          <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
            <Heading variant={Heading.TYPES.H3} className="pb-12">
              Context
            </Heading>
            {subOrganizationEnabled ? (
              <div className="mb-12">
                <SubOrgMultiSelectWithChipsFormik
                  id={FIELD_IDS.SUB_ORGANIZATION_IDS}
                  label="Sub Organizations"
                  showOnly={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            ) : null}
            <div className="mb-4">
              <LobMultiSelectWithChipsFormik
                lobsFieldId={FIELD_IDS.LOBS}
                allLobsFieldId={FIELD_IDS.ALL_LOBS}
                {...(subOrganizationEnabled
                  ? {
                      subOrganizationIds: isAllSubOrganizationsSelected(subOrganizationsValue)
                        ? subOrganizations.map((subOrg) => subOrg.id)
                        : subOrganizationsValue,
                      subOrgFieldId: FIELD_IDS.SUB_ORGANIZATION_IDS,
                      shouldResetOnSubOrgChange: true,
                      disabled: isEmpty(subOrganizationsValue),
                    }
                  : {})}
              />
            </div>
          </div>
          <EventSelectField
            eventKeys={supportedEvents ? sortKeysByField(supportedEvents, 'desc') : []}
            supportedEvents={supportedEvents}
            disabled={isSubmitting}
          />
        </>
      )}
      <div className="mt-20 rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          Type
        </Heading>
        {/* One control on both surfaces. A claim-action guardrail is an AI rule by construction, so
            it lands on the same Warning-disabled / Block-selected state the Guardrails screen shows
            for any rule whose conditions read `ai.*` — rather than a second, differently-shaped
            read-only view of the same fact. */}
        <RadioButtonGroupFormik
          id={FIELD_IDS.OUTCOME}
          direction="column"
          buttonVariant="default"
          showOnly={isSubmitting}
          options={outcomeOptions}
        />
        <TextFieldFormik
          id={FIELD_IDS.MESSAGE}
          label="Message"
          className={cn(classes.textField, 'mt-12')}
          fullWidth
          multiline
          rows={3}
          disabled={isDisabled}
          helperText={presetContext ? 'Shown to the adjuster who picks up the escalated action.' : undefined}
        />
      </div>
      <div className="px-4 py-20">
        <GuardrailConditionsQueryBuilderFormik
          fieldId={FIELD_IDS.CONDITIONS_JSON}
          eventKey={eventKey || ''}
          // Every picked LOB: the catalog is the cross-LOB intersection, which is
          // also what save-time validation checks the rule against.
          lobs={catalogLobs}
          isAllLobs={isAllLobs}
          isAllSubOrgs={subOrganizationEnabled ? isAllSubOrganizationsSelected(values.sub_organization_ids) : true}
          subOrgIds={subOrganizationEnabled ? values.sub_organization_ids : []}
          onLoadingChange={setIsLoadingConditions}
          // The AI-agent leaf defines what a claim-action guardrail *is* — without it the rule
          // gates the same event an adjuster fires manually. Locked rather than merely seeded, so
          // it cannot be edited or deleted back into a rule that stops the human it escalates to.
          // The action's required conditions are locked for the same reason: without them a
          // reopen's guardrail would gate every close, which reports the same event.
          lockedFieldPaths={
            presetContext
              ? [AI_AGENT_CONDITION_FIELD, ...(presetContext.requiredConditions ?? []).map(({ field }) => field)]
              : undefined
          }
        />
      </div>

      <ButtonsContainer>
        <CancelButton onClick={onCancel} disabled={isSubmitting} />
        <Button color="primary" onClick={() => handleSubmit()} disabled={isDisabled}>
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </ButtonsContainer>
    </CardDialog>
  );
};

interface EventSelectFieldProps {
  /** The selectable events, in the order to list them. */
  eventKeys: string[];
  supportedEvents?: Record<string, GuardrailSupportedEventsResponseGuardrailSupportedEventModel>;
  disabled: boolean;
}

/** The Event picker: every supported event on the Guardrails screen, the action's own events in a
 *  claim action's dialog. */
const EventSelectField: React.FC<EventSelectFieldProps> = ({ eventKeys, supportedEvents, disabled }) => {
  const { classes } = useStyles();
  return (
    <TextFieldFormik
      id={FIELD_IDS.TRIGGER_EVENT}
      label="Event"
      className={cn(classes.textField, 'mt-20 w-1/2')}
      fullWidth
      disabled={disabled}
      select
    >
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore -- TextFieldFormik is a JS component; its children prop infers as null|undefined */}
      {!isEmpty(eventKeys) ? (
        eventKeys.map((key) => (
          <MenuItem key={key} value={key}>
            {supportedEvents?.[key]?.desc ?? key}
          </MenuItem>
        ))
      ) : (
        <MenuItem value="" disabled>
          No events available
        </MenuItem>
      )}
    </TextFieldFormik>
  );
};

interface ReadOnlyContextProps {
  /** Omitted when the user picks the event among the action's own, outside this box. */
  eventLabel?: string;
  subOrganizationEnabled: boolean;
  /** Always an array: `AddRuleInner` normalises the `false` a flat organization gets. */
  subOrganizations: { id: number; name?: string }[];
}

/** The Context box, rendered as fixed chips. Used when the guardrail belongs to a claim action,
 *  whose scope the user configures on the action rather than here — and its event too, unless the
 *  action reports more than one.
 *
 *  The chips read the Formik values, not the preset: on an existing rule those are the rule's own
 *  scope, which the filter deliberately lets differ from the action's (an all-LOBs rule is listed
 *  under an action scoped to one LOB). Rendering the preset would state a scope that Save does not
 *  write. */
const ReadOnlyContext: React.FC<ReadOnlyContextProps> = ({ eventLabel, subOrganizationEnabled, subOrganizations }) => {
  const { values } = useFormikContext<FormValues>();
  const { lobConfigurationsDict } = useLobConfiguration();
  const subOrgIds = values.sub_organization_ids ?? [];
  const subOrgNames = isAllSubOrganizationsSelected(subOrgIds)
    ? ['All']
    : (subOrganizations
        .filter((subOrg) => subOrgIds.includes(subOrg.id))
        .map((subOrg) => subOrg.name)
        .filter(Boolean) as string[]);
  const lobLabels = values.is_all_lobs
    ? ['All']
    : // A LOB missing from both the org config and CLAIM_TYPES_CONFIGURATION has no description;
      // fall back to its key so the chip names the LOB instead of rendering blank.
      concreteSelectedLobs(values.lobs ?? []).map(
        (lobKey) => getLobDescription(lobKey, lobConfigurationsDict) ?? lobKey
      );

  // `chipLabels`, not `values` — the Formik bag above already owns that name in this scope.
  const row = (label: string, chipLabels: string[]) => (
    <div className="mb-12">
      <Text variant={Text.VARIANTS.XXS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {label}
      </Text>
      <div className="mt-4 border-0 border-b border-solid border-slate-400 pb-8">
        {chipLabels.length ? (
          chipLabels.map((value) => <Chip key={value} label={value} size="small" className="mr-8" />)
        ) : (
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
            —
          </Text>
        )}
      </div>
    </div>
  );

  return (
    <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
      <Heading variant={Heading.TYPES.H3} className="pb-12">
        Context
      </Heading>
      {subOrganizationEnabled ? row('Sub Organization', subOrgNames) : null}
      {row('Lines of Business', lobLabels)}
      {isNil(eventLabel) ? null : row('Event', [eventLabel])}
    </div>
  );
};

export default AddOrEditGuardrailRuleDialog;
// AddRuleInner is exported for testing the Formik wiring (scope-change reset of
// conditions). Production code should use the default export.
export { AddRuleInner };
